import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../../components/admin/AdminShell';
import { useCreateWACampaign, useSendWACampaign, usePreviewSegment, useExpandSegment, useWATemplates, useSyncWATemplates } from '../../../hooks/useWhatsAppQueries';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle2, Send, Users, Filter, Loader2, AlertTriangle, RefreshCw, MessageSquare } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card } from '../../../components/ui/card';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';

const STEPS = ['Template', 'Variables', 'Contacts', 'Review & Send'];

/**
 * Substitute {{1}}, {{2}}, … placeholders in a template body with concrete
 * values. Empty values render as a dim grey placeholder so the operator
 * sees exactly which slots are still blank.
 */
function renderBodyPreview(body, vars) {
  if (!body) return null;
  return body.replace(/\{\{(\d+)\}\}/g, (_m, n) => {
    const idx = parseInt(n, 10) - 1;
    const v   = vars?.[idx];
    return v && String(v).trim() ? String(v) : `«${n}»`;
  });
}

export default function WhatsAppCampaignWizardPage() {
  const navigate = useNavigate();
  const createCampaign = useCreateWACampaign();
  const sendCampaign   = useSendWACampaign();
  const previewSegment = usePreviewSegment();
  const expandSegment  = useExpandSegment();
  const syncTemplates  = useSyncWATemplates();

  // Pull APPROVED templates only — those are the ONLY ones Meta will accept
  // for outbound business-initiated sends.
  const { data: tplData, isLoading: tplLoading } = useWATemplates({ status: 'APPROVED' });
  const approvedTemplates = useMemo(() => tplData?.items || [], [tplData]);

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '',
    template_name: '',
    language_code: '',
    variables: [],
    contacts: [],
    contactsRaw: '',
  });

  // ── Segmentation state ────────────────────────────────────────────────────
  const [segMode, setSegMode] = useState('manual');   // 'manual' | 'segment'
  const [seg, setSeg] = useState({
    customer_type:        '',
    whatsapp_opt_in:      true,
    marketing_opt_in:     true,
    min_total_visits:     '',
    visited_since_days:   '',
    not_visited_for_days: '',
    only_with_email:      false,
  });
  const [segPreview, setSegPreview] = useState(null);  // { total, sample, phones[] }

  const updateForm = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateSeg  = (k, v) => setSeg(s => ({ ...s, [k]: v }));

  // ── Selected template lookup ──────────────────────────────────────────────
  // Identify the chosen template doc so we know variable_count + body + lang.
  // The Select uses `${name}|${language}` as the value so identical template
  // names in multiple languages don't collide.
  const selectedTemplate = useMemo(() => {
    if (!form.template_name) return null;
    return approvedTemplates.find(
      (t) => t.name === form.template_name && t.language === form.language_code
    ) || null;
  }, [approvedTemplates, form.template_name, form.language_code]);

  // Auto-size the variables array whenever the selected template changes.
  // We deliberately preserve any partially-typed values when re-sizing.
  useEffect(() => {
    if (!selectedTemplate) return;
    const need = selectedTemplate.variable_count || 0;
    setForm((f) => {
      const next = [...(f.variables || [])];
      if (next.length === need) return f;
      if (next.length < need) {
        return { ...f, variables: [...next, ...Array(need - next.length).fill('')] };
      }
      return { ...f, variables: next.slice(0, need) };
    });
  }, [selectedTemplate]);

  const handleSelectTemplate = (combined) => {
    if (!combined) return;
    const [name, language] = combined.split('|');
    setForm((f) => ({ ...f, template_name: name, language_code: language }));
  };

  const handleSyncTemplates = async () => {
    try {
      const res = await syncTemplates.mutateAsync();
      const created = res?.data?.created || 0;
      const updated = res?.data?.updated || 0;
      toast.success(`Synced templates from Meta · ${created} new, ${updated} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Template sync failed');
    }
  };

  const handleSegmentPreview = async () => {
    const payload = {
      customer_type:        seg.customer_type || null,
      whatsapp_opt_in:      seg.whatsapp_opt_in,
      marketing_opt_in:     seg.marketing_opt_in,
      min_total_visits:     seg.min_total_visits ? parseInt(seg.min_total_visits) : null,
      visited_since_days:   seg.visited_since_days ? parseInt(seg.visited_since_days) : null,
      not_visited_for_days: seg.not_visited_for_days ? parseInt(seg.not_visited_for_days) : null,
      only_with_email:      seg.only_with_email,
    };
    try {
      const res = await previewSegment.mutateAsync(payload);
      setSegPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    }
  };

  const handleApplySegment = async () => {
    if (!segPreview) return;
    try {
      const payload = {
        customer_type:        seg.customer_type || null,
        whatsapp_opt_in:      seg.whatsapp_opt_in,
        marketing_opt_in:     seg.marketing_opt_in,
        min_total_visits:     seg.min_total_visits ? parseInt(seg.min_total_visits) : null,
        visited_since_days:   seg.visited_since_days ? parseInt(seg.visited_since_days) : null,
        not_visited_for_days: seg.not_visited_for_days ? parseInt(seg.not_visited_for_days) : null,
        only_with_email:      seg.only_with_email,
      };
      const res = await expandSegment.mutateAsync(payload);
      const phones = res.data?.contacts || [];
      updateForm('contactsRaw', phones.join('\n'));
      if (res.data?.capped) {
        toast.warning(`Loaded first ${phones.length} contacts (capped). Refine the segment to reduce audience size.`);
      } else {
        toast.success(`✅ Segment applied — ${phones.length} contacts loaded`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to expand segment');
    }
  };

  // ── Step validation ──────────────────────────────────────────────────────
  // Gate every step so the user CANNOT submit a half-built campaign that
  // would just get rejected by Meta with the cryptic "(#132000) number of
  // parameters does not match expected" error.
  const allVariablesFilled =
    !selectedTemplate ||
    selectedTemplate.variable_count === 0 ||
    (form.variables.length === selectedTemplate.variable_count &&
     form.variables.every((v) => String(v || '').trim().length > 0));

  const canNext = () => {
    if (step === 0) {
      return form.name.trim() && !!selectedTemplate;
    }
    if (step === 1) {
      return allVariablesFilled;
    }
    if (step === 2) {
      const phones = parseContacts(form.contactsRaw);
      return phones.length > 0;
    }
    return true;
  };

  const parseContacts = (raw) =>
    raw.split(/[\n,;]+/).map(s => s.trim()).filter(s => s.length >= 10);

  const handleNext = () => {
    if (step === 2) {
      updateForm('contacts', parseContacts(form.contactsRaw));
    }
    setStep(s => s + 1);
  };

  const handleSubmit = async (sendNow) => {
    if (sendNow && !allVariablesFilled) {
      toast.error('Fill every template variable before sending.');
      setStep(1);
      return;
    }
    const contacts = parseContacts(form.contactsRaw);
    try {
      const res = await createCampaign.mutateAsync({
        name: form.name,
        template_name: form.template_name,
        language_code: form.language_code,
        variables: form.variables,
        contacts,
      });
      const campaignId = res.data._id;
      if (sendNow) {
        await sendCampaign.mutateAsync(campaignId);
        toast.success(`Campaign created and sending to ${contacts.length} contacts!`);
      } else {
        toast.success('Campaign saved as draft.');
      }
      navigate('/admin/marketing/whatsapp');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create campaign');
    }
  };

  const isSubmitting = createCampaign.isPending || sendCampaign.isPending;

  return (
    <AdminShell title="New WhatsApp Campaign">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-6 text-sm text-[#B9B9B9]">
        <button onClick={() => navigate('/admin/marketing/whatsapp')} className="hover:text-[#F2F2F2] flex items-center gap-1">
          <ArrowLeft size={14}/> WhatsApp Campaigns
        </button>
        <span>/</span>
        <span className="text-[#F2F2F2]">New Campaign</span>
      </div>

      {/* Step Progress */}
      <div className="flex items-center gap-0 mb-10 max-w-xl">
        {STEPS.map((label, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                i < step ? 'bg-[#25D366] text-black' :
                i === step ? 'bg-[rgba(37,211,102,0.15)] border-2 border-[#25D366] text-[#25D366]' :
                'bg-[rgba(255,255,255,0.06)] text-[#6B6B6B]'
              }`}>
                {i < step ? <CheckCircle2 size={14}/> : i + 1}
              </div>
              <span className={`text-[10px] whitespace-nowrap ${
                i === step ? 'text-[#25D366]' : i < step ? 'text-[#4ADE80]' : 'text-[#6B6B6B]'
              }`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-px mx-1 mt-[-12px] ${
                i < step ? 'bg-[#25D366]' : 'bg-[rgba(255,255,255,0.08)]'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="max-w-2xl">
        {/* Step 0: Template */}
        {step === 0 && (
          <Card className="p-8 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-2xl space-y-6">
            <h2 className="text-xl font-semibold text-[#F2F2F2]">Campaign Details</h2>

            <div className="space-y-2">
              <Label className="text-[#B9B9B9]">Campaign Name</Label>
              <Input
                value={form.name}
                onChange={e => updateForm('name', e.target.value)}
                placeholder="e.g. Weekend Event Promo"
                className="bg-[rgba(255,255,255,0.04)] border-[rgba(255,255,255,0.1)] text-[#F2F2F2]"
                data-testid="wa-campaign-name"
              />
              <p className="text-[11px] text-[#6B6B6B]">Internal name only — recipients don't see it.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[#B9B9B9]">WhatsApp Template</Label>
                <button
                  type="button"
                  onClick={handleSyncTemplates}
                  disabled={syncTemplates.isPending}
                  className="text-[11px] text-[#25D366] hover:text-[#4ADE80] flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="wa-sync-templates-btn"
                >
                  {syncTemplates.isPending
                    ? <Loader2 size={11} className="animate-spin"/>
                    : <RefreshCw size={11}/>} Sync from Meta
                </button>
              </div>
              {tplLoading ? (
                <div className="h-10 flex items-center px-3 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-xs text-[#6B6B6B]">
                  <Loader2 size={12} className="animate-spin mr-2"/> Loading approved templates…
                </div>
              ) : approvedTemplates.length === 0 ? (
                <div className="p-3 rounded-xl bg-[rgba(255,77,77,0.06)] border border-[rgba(255,77,77,0.25)] text-xs text-[#FF8A8A] flex items-start gap-2">
                  <AlertTriangle size={13} className="flex-shrink-0 mt-0.5"/>
                  <span>
                    No approved WhatsApp templates synced yet. Click <strong>Sync from Meta</strong> above,
                    or visit Marketing → WhatsApp → Templates first.
                  </span>
                </div>
              ) : (
                <Select
                  value={selectedTemplate ? `${selectedTemplate.name}|${selectedTemplate.language}` : ''}
                  onValueChange={handleSelectTemplate}
                >
                  <SelectTrigger
                    data-testid="wa-template-select"
                    className="bg-[rgba(255,255,255,0.04)] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] h-12"
                  >
                    <SelectValue placeholder="Choose an approved template…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-[rgba(255,255,255,0.1)] text-[#F2F2F2]">
                    {approvedTemplates.map((t) => (
                      <SelectItem
                        key={`${t.name}|${t.language}`}
                        value={`${t.name}|${t.language}`}
                        className="py-2"
                      >
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <code className="text-[#25D366] text-xs">{t.name}</code>
                            <span className="text-[10px] text-[#6B6B6B] uppercase">{t.language}</span>
                            <Badge
                              variant="outline"
                              className="text-[9px] border-[rgba(212,168,67,0.4)] text-[#D4A843] py-0 px-1.5"
                            >
                              {t.category || 'UTILITY'}
                            </Badge>
                            <span className="text-[10px] text-[#B9B9B9]">
                              {t.variable_count === 0
                                ? 'no variables'
                                : `${t.variable_count} variable${t.variable_count === 1 ? '' : 's'}`}
                            </span>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-[11px] text-[#6B6B6B]">
                Only APPROVED templates from your WABA can send. Need a new template?
                Create it in Meta Business Manager, then click "Sync from Meta".
              </p>
            </div>

            {/* Live preview of the selected template */}
            {selectedTemplate && (
              <div
                data-testid="wa-template-preview"
                className="p-4 rounded-xl bg-[rgba(37,211,102,0.05)] border border-[rgba(37,211,102,0.18)] space-y-2"
              >
                <div className="flex items-center gap-2 text-[11px] text-[#25D366] uppercase tracking-widest">
                  <MessageSquare size={12}/> Template Preview
                </div>
                <p className="text-sm text-[#F2F2F2] whitespace-pre-wrap font-mono leading-relaxed">
                  {selectedTemplate.body_preview}
                </p>
                <p className="text-[11px] text-[#6B6B6B]">
                  Language: <code className="text-[#D4A843]">{selectedTemplate.language}</code> ·
                  Variables: <strong>{selectedTemplate.variable_count}</strong>
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Step 1: Variables */}
        {step === 1 && (
          <Card className="p-8 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-2xl space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-[#F2F2F2]">Template Variables</h2>
              <p className="text-sm text-[#B9B9B9] mt-1">
                Fill every placeholder. The same values are sent to all recipients in this campaign.
              </p>
            </div>

            {/* Selected template summary */}
            {selectedTemplate ? (
              <div className="p-3 rounded-xl bg-[rgba(37,211,102,0.05)] border border-[rgba(37,211,102,0.15)] flex items-start gap-2">
                <CheckCircle2 size={14} className="text-[#25D366] flex-shrink-0 mt-0.5"/>
                <div className="text-xs">
                  <p className="text-[#F2F2F2]">
                    <code className="text-[#25D366]">{selectedTemplate.name}</code>
                    {' '}({selectedTemplate.language}) ·
                    {' '}<strong>{selectedTemplate.variable_count}</strong> variable
                    {selectedTemplate.variable_count === 1 ? '' : 's'} required
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-xl bg-[rgba(255,77,77,0.06)] border border-[rgba(255,77,77,0.25)] text-xs text-[#FF8A8A] flex items-center gap-2">
                <AlertTriangle size={13}/> Go back and pick a template first.
              </div>
            )}

            {selectedTemplate && selectedTemplate.variable_count > 0 && (
              <div className="space-y-3">
                {Array.from({ length: selectedTemplate.variable_count }).map((_, i) => (
                  <div key={i} className="space-y-1.5">
                    <Label className="text-[#B9B9B9] text-xs flex items-center gap-2">
                      <code className="text-[#D4A843]">{'{{' + (i + 1) + '}}'}</code>
                      <span>Variable {i + 1}</span>
                    </Label>
                    <Input
                      value={form.variables[i] || ''}
                      onChange={e => {
                        const vars = [...form.variables];
                        vars[i] = e.target.value;
                        updateForm('variables', vars);
                      }}
                      placeholder={`Value for {{${i + 1}}}`}
                      className="bg-[rgba(255,255,255,0.04)] border-[rgba(255,255,255,0.1)] text-[#F2F2F2]"
                      data-testid={`wa-variable-input-${i + 1}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {selectedTemplate && selectedTemplate.variable_count === 0 && (
              <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.04)] text-xs text-[#B9B9B9]">
                ✓ This template has no variables. Continue to the next step.
              </div>
            )}

            {/* Live rendered preview */}
            {selectedTemplate && (
              <div className="p-4 rounded-xl bg-[rgba(37,211,102,0.05)] border border-[rgba(37,211,102,0.18)] space-y-2">
                <p className="text-[11px] text-[#25D366] uppercase tracking-widest flex items-center gap-1.5">
                  <MessageSquare size={11}/> Live Message Preview
                </p>
                <p
                  data-testid="wa-rendered-preview"
                  className="text-sm text-[#F2F2F2] whitespace-pre-wrap font-mono leading-relaxed"
                >
                  {renderBodyPreview(selectedTemplate.body_preview, form.variables)}
                </p>
                <p className="text-[10px] text-[#6B6B6B]">
                  Placeholders shown as «1», «2» etc. are still blank.
                </p>
              </div>
            )}

            {/* Personalization warning — broadcast-only architecture */}
            {selectedTemplate && selectedTemplate.variable_count > 0 && (
              <div className="p-3 rounded-xl bg-[rgba(212,168,67,0.06)] border border-[rgba(212,168,67,0.18)] text-xs text-[#D4A843] flex items-start gap-2">
                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5"/>
                <span>
                  Every recipient receives the <strong>same</strong> values. For per-customer
                  personalisation (e.g. name), use a template with no variables or
                  contact your developer to enable CSV-based merge fields.
                </span>
              </div>
            )}
          </Card>
        )}

        {/* Step 2: Contacts */}
        {step === 2 && (
          <Card className="p-8 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-2xl space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-[#F2F2F2]">Recipients</h2>
              <p className="text-sm text-[#B9B9B9] mt-1">Paste numbers manually OR build a segment from your customer database.</p>
            </div>

            {/* Mode tabs */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSegMode('manual')}
                data-testid="wa-recipients-mode-manual"
                className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  segMode === 'manual'
                    ? 'bg-[#25D366] text-black'
                    : 'bg-[rgba(255,255,255,0.04)] text-[#B9B9B9] hover:bg-[rgba(255,255,255,0.08)]'
                }`}
              >
                <Users size={14} className="inline mr-2" /> Manual List
              </button>
              <button
                type="button"
                onClick={() => setSegMode('segment')}
                data-testid="wa-recipients-mode-segment"
                className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  segMode === 'segment'
                    ? 'bg-[#25D366] text-black'
                    : 'bg-[rgba(255,255,255,0.04)] text-[#B9B9B9] hover:bg-[rgba(255,255,255,0.08)]'
                }`}
              >
                <Filter size={14} className="inline mr-2" /> Build Segment
              </button>
            </div>

            {/* Segment Builder */}
            {segMode === 'segment' && (
              <div className="space-y-4 p-5 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)]" data-testid="wa-segment-builder">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-[#B9B9B9] text-xs">Customer Type</Label>
                    <Select value={seg.customer_type || 'any'} onValueChange={(v) => updateSeg('customer_type', v === 'any' ? '' : v)}>
                      <SelectTrigger data-testid="seg-customer-type" className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2]">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F0F0F] border-[rgba(255,255,255,0.1)] text-[#F2F2F2]">
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="old_customer">Old Customer</SelectItem>
                        <SelectItem value="elite">Elite</SelectItem>
                        <SelectItem value="vip">VIP</SelectItem>
                        <SelectItem value="promoter">Promoter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-[#B9B9B9] text-xs">Min Total Visits</Label>
                    <Input
                      type="number"
                      min={0}
                      data-testid="seg-min-visits"
                      placeholder="e.g. 3"
                      value={seg.min_total_visits}
                      onChange={(e) => updateSeg('min_total_visits', e.target.value)}
                      className="bg-[#111] border-[rgba(255,255,255,0.1)]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-[#B9B9B9] text-xs">Visited in last N days</Label>
                    <Input
                      type="number"
                      min={0}
                      data-testid="seg-visited-since"
                      placeholder="e.g. 30"
                      value={seg.visited_since_days}
                      onChange={(e) => updateSeg('visited_since_days', e.target.value)}
                      className="bg-[#111] border-[rgba(255,255,255,0.1)]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-[#B9B9B9] text-xs">Not visited for N+ days (win-back)</Label>
                    <Input
                      type="number"
                      min={0}
                      data-testid="seg-not-visited"
                      placeholder="e.g. 60"
                      value={seg.not_visited_for_days}
                      onChange={(e) => updateSeg('not_visited_for_days', e.target.value)}
                      className="bg-[#111] border-[rgba(255,255,255,0.1)]"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-5 pt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid="seg-whatsapp-optin"
                      checked={seg.whatsapp_opt_in}
                      onCheckedChange={(v) => updateSeg('whatsapp_opt_in', v)}
                      className="data-[state=checked]:bg-[#25D366]"
                    />
                    <Label className="text-[#B9B9B9] text-xs">WhatsApp opt-in required</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid="seg-marketing-optin"
                      checked={seg.marketing_opt_in}
                      onCheckedChange={(v) => updateSeg('marketing_opt_in', v)}
                      className="data-[state=checked]:bg-[#25D366]"
                    />
                    <Label className="text-[#B9B9B9] text-xs">Marketing opt-in required</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid="seg-only-email"
                      checked={seg.only_with_email}
                      onCheckedChange={(v) => updateSeg('only_with_email', v)}
                      className="data-[state=checked]:bg-[#25D366]"
                    />
                    <Label className="text-[#B9B9B9] text-xs">Only customers with email</Label>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-[rgba(255,255,255,0.07)]">
                  <Button
                    type="button"
                    onClick={handleSegmentPreview}
                    disabled={previewSegment.isPending}
                    variant="outline"
                    className="gap-2 bg-transparent border-[rgba(255,255,255,0.1)] text-[#F2F2F2]"
                    data-testid="seg-preview-btn"
                  >
                    {previewSegment.isPending ? <Loader2 size={14} className="animate-spin" /> : <Filter size={14} />}
                    Preview Audience
                  </Button>
                  {segPreview && (
                    <>
                      <div className="px-4 py-2 rounded-xl bg-[rgba(37,211,102,0.08)] border border-[rgba(37,211,102,0.2)]" data-testid="seg-preview-result">
                        <span className="text-xs text-[#B9B9B9]">Matching: </span>
                        <span className="text-lg font-bold text-[#25D366] font-mono">{segPreview.total}</span>
                        <span className="text-xs text-[#B9B9B9]"> customer{segPreview.total === 1 ? '' : 's'}</span>
                      </div>
                      <Button
                        type="button"
                        onClick={handleApplySegment}
                        disabled={expandSegment.isPending || segPreview.total === 0}
                        style={{ background: '#25D366', color: '#000' }}
                        className="gap-2 rounded-xl"
                        data-testid="seg-apply-btn"
                      >
                        {expandSegment.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                        Apply Segment ({segPreview.total})
                      </Button>
                    </>
                  )}
                </div>

                {segPreview && segPreview.sample && segPreview.sample.length > 0 && (
                  <div className="space-y-1 pt-2">
                    <p className="text-xs text-[#6B6B6B] uppercase tracking-widest">Sample (first 5)</p>
                    {segPreview.sample.map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-xs text-[#B9B9B9] py-1 border-b border-[rgba(255,255,255,0.04)]">
                        <span>{c.full_name || '(no name)'} · <code className="text-[#D4A843]">{c.phone_e164 || c.phone}</code></span>
                        <span className="text-[#6B6B6B]">{c.customer_type || 'basic'} · {c.total_visits || 0} visits</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Manual textarea (always editable — gets populated by segment apply too) */}
            <div className="space-y-2">
              <Label className="text-[#B9B9B9] text-xs">Phone Numbers</Label>
              <Textarea
                value={form.contactsRaw}
                onChange={e => updateForm('contactsRaw', e.target.value)}
                placeholder={`9876543210\n9123456789\n+917000000000`}
                rows={10}
                className="font-mono text-sm bg-[rgba(255,255,255,0.04)] border-[rgba(255,255,255,0.1)] text-[#F2F2F2]"
                data-testid="wa-contacts-input"
              />
              {form.contactsRaw.trim() && (
                <p className="text-sm text-[#25D366]">
                  ✓ {parseContacts(form.contactsRaw).length} valid number(s) detected
                </p>
              )}
            </div>
          </Card>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <Card className="p-8 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-2xl space-y-6">
            <h2 className="text-xl font-semibold text-[#F2F2F2]">Review & Send</h2>
            <div className="space-y-4">
              <Row label="Campaign Name" value={form.name} />
              <Row label="Template" value={<code className="text-[#25D366]">{form.template_name}</code>} />
              <Row label="Language" value={form.language_code} />
              <Row
                label="Variables"
                value={
                  selectedTemplate && selectedTemplate.variable_count > 0
                    ? form.variables.map((v, i) => (
                        <span key={i} className="inline-flex items-center gap-1 mr-2">
                          <code className="text-[#D4A843]">{`{{${i + 1}}}`}</code>
                          <span className="text-[#F2F2F2]">= {v ? `"${v}"` : <em className="text-[#FF8A8A]">blank</em>}</span>
                        </span>
                      ))
                    : '—'
                }
              />
              <Row label="Recipients" value={`${parseContacts(form.contactsRaw).length} contacts`} />
            </div>

            {/* Rendered message preview */}
            {selectedTemplate && (
              <div
                data-testid="wa-review-preview"
                className="p-4 rounded-xl bg-[rgba(37,211,102,0.06)] border border-[rgba(37,211,102,0.18)]"
              >
                <p className="text-[11px] text-[#25D366] font-semibold mb-2 uppercase tracking-widest flex items-center gap-1.5">
                  <MessageSquare size={11}/> Message Recipients Will See
                </p>
                <p className="text-sm text-[#F2F2F2] whitespace-pre-wrap font-mono leading-relaxed">
                  {renderBodyPreview(selectedTemplate.body_preview, form.variables)}
                </p>
              </div>
            )}

            {!allVariablesFilled && (
              <div className="p-3 rounded-xl bg-[rgba(255,77,77,0.06)] border border-[rgba(255,77,77,0.3)] flex items-start gap-2 text-xs text-[#FF8A8A]">
                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5"/>
                <span>
                  Some variables are still blank. Meta will reject every send.
                  Go back to step 2 and fill them in.
                </span>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => handleSubmit(false)}
                disabled={isSubmitting}
                variant="outline"
                className="flex-1 bg-transparent border-[rgba(255,255,255,0.1)] text-[#B9B9B9]"
                data-testid="wa-save-draft-btn"
              >
                Save as Draft
              </Button>
              <Button
                onClick={() => handleSubmit(true)}
                disabled={isSubmitting || !allVariablesFilled}
                className="flex-1 gap-2"
                style={
                  !isSubmitting && allVariablesFilled
                    ? { background: '#25D366', color: '#000' }
                    : {}
                }
                data-testid="wa-send-now-btn"
              >
                <Send size={16}/>
                {isSubmitting ? 'Sending…' : 'Send Now'}
              </Button>
            </div>
          </Card>
        )}

        {/* Step Nav Buttons */}
        {step < 3 && (
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/admin/marketing/whatsapp')}
              className="bg-transparent border-[rgba(255,255,255,0.1)] text-[#B9B9B9] gap-2"
            >
              <ArrowLeft size={15}/> {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canNext()}
              style={canNext() ? {background:'#25D366', color:'#000'} : {}}
              className="gap-2"
            >
              Next <ArrowRight size={15}/>
            </Button>
          </div>
        )}
      </div>
    </AdminShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center gap-4">
      <span className="text-[#6B6B6B] text-sm w-36 flex-shrink-0">{label}</span>
      <span className="text-[#F2F2F2] text-sm">{value}</span>
    </div>
  );
}
